import React, { useState, useEffect, useMemo } from 'react';
import Swal from 'sweetalert2';
import axios from 'axios';
import Header from '../../Components/Header';
import { Spinner } from 'react-bootstrap';
import { BASE_URL, paybridge_Url, getRsaActivateUrl } from '../../Config';
import {
  isRsaSaleContext,
  normalizeAgentPackages,
} from '../../utils/rsaSale';
import AppLoader from '../../Components/AppLoader';
import '../Company/Company.css';
import 'bootstrap-icons/font/bootstrap-icons.css';

const TYPE_OF_PAYMENT_OPTIONS = ['Cash', 'Wallet Payment', 'Card'];

const PAYBRIDGE_CODE_ALERTS = {
  '0000': {
    icon: 'success',
    title: 'SUCCESS',
    text: 'Operation completed successfully.',
  },
  '1001': {
    icon: 'error',
    title: 'VALIDATION_ERROR',
    text: 'Missing or invalid request parameters.',
  },
  '1002': {
    icon: 'error',
    title: 'BAD_REQUEST',
    text: 'Business validation failed (e.g. invalid productCode or no subscription).',
  },
  '1006': {
    icon: 'warning',
    title: 'PAYMENT_PENDING',
    text: 'Payment link already generated and awaiting user payment.',
  },
  '1007': {
    icon: 'warning',
    title: 'ALREADY_SUBSCRIBED',
    text: 'Customer already has an active policy for this product.',
  },
  '401': {
    icon: 'error',
    title: 'UNAUTHORIZED',
    text: 'Invalid or missing X-Auth-Key header or partner mismatch.',
  },
  '2000': {
    icon: 'error',
    title: 'INTERNAL_ERROR',
    text: 'Internal server error.',
  },
  '3000': {
    icon: 'error',
    title: 'EXTERNAL_API_ERROR',
    text: 'Payment gateway communication error.',
  },
};

const STATUS_NAME_TO_CODE = {
  SUCCESS: '0000',
  VALIDATION_ERROR: '1001',
  BAD_REQUEST: '1002',
  PAYMENT_PENDING: '1006',
  ALREADY_SUBSCRIBED: '1007',
  UNAUTHORIZED: '401',
  INTERNAL_ERROR: '2000',
  EXTERNAL_API_ERROR: '3000',
};

const extractPaybridgeCode = (resBody = {}) => {
  const raw =
    resBody.response_code ??
    resBody.responseCode ??
    resBody.code ??
    resBody.Code ??
    resBody.statusCode ??
    resBody.StatusCode;

  if (raw !== undefined && raw !== null && String(raw).trim() !== '') {
    return String(raw).trim();
  }

  const statusName = String(resBody.status || resBody.Status || '').toUpperCase();
  if (STATUS_NAME_TO_CODE[statusName]) return STATUS_NAME_TO_CODE[statusName];

  return null;
};

const showPaybridgeCodeAlert = (resBody = {}) => {
  const code = extractPaybridgeCode(resBody);
  const mapped = code ? PAYBRIDGE_CODE_ALERTS[code] : null;
  const apiMessage = resBody.message || resBody.Message || '';

  if (mapped) {
    Swal.fire({
      icon: mapped.icon,
      title: mapped.title,
      text: apiMessage || mapped.text,
    }).then(() => {
      if (code === '0000') window.location.reload();
    });
    return true;
  }

  return false;
};

const getRsaResponseDetails = (responseBody = {}) => {
  const result =
    responseBody?.ActivateRSAOnlinePortalResult ||
    responseBody?.activateRSAOnlinePortalResult ||
    responseBody?.data?.ActivateRSAOnlinePortalResult ||
    responseBody?.data?.activateRSAOnlinePortalResult ||
    responseBody?.data ||
    responseBody;

  const responseCode =
    result?.ResponseCode ??
    result?.responseCode ??
    result?.Response_Code ??
    result?.response_code ??
    result?.statusCode ??
    result?.StatusCode ??
    responseBody?.ResponseCode ??
    responseBody?.responseCode ??
    responseBody?.response_code;

  const message =
    result?.Message ||
    result?.message ||
    result?.ResponseMessage ||
    result?.responseMessage ||
    responseBody?.Message ||
    responseBody?.message ||
    responseBody?.error;

  const transactionId =
    result?.TransectionID ||
    result?.TransactionID ||
    result?.transactionId ||
    result?.transactionID ||
    result?.txnId;

  const normalizedCode = String(responseCode ?? '').trim().toUpperCase();
  const normalizedMessage = String(message || '').toLowerCase();
  const successfulCode = ['0000', '000', '200', 'OK', 'SUCCESS'].includes(normalizedCode);

  return {
    message,
    responseCode,
    transactionId,
    ok:
      successfulCode ||
      responseBody?.success === true ||
      responseBody?.status === true ||
      result?.success === true ||
      result?.status === true ||
      normalizedMessage.includes('success'),
  };
};

const getRsaErrorMessage = (error) => {
  const responseBody = error?.response?.data;
  const status = error?.response?.status;

  if (typeof responseBody === 'string') {
    const plainText = responseBody.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    if (/object reference not set/i.test(plainText)) {
      return `RSA service error (${status || 'unknown status'}). Please verify the selected package and customer details.`;
    }
    if (plainText && plainText.length < 240) return plainText;
  }

  return (
    responseBody?.message ||
    responseBody?.Message ||
    responseBody?.error ||
    (status ? `RSA service rejected the request (HTTP ${status}).` : null) ||
    error?.message ||
    'Unable to activate RSA. Please try again.'
  );
};

const Sales = () => {
  const [agentId, setAgentId] = useState('');
  const [agentName, setAgentName] = useState('');
  const [CustomerName, setCustomerName] = useState('');
  const [mobileNo, setMobileNo] = useState('');
  const [cnic, setCnic] = useState('');
  const [gender, setGender] = useState('');
  const [packageId, setPackageId] = useState('');
  const [Remarks, setRemarks] = useState('');
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [companyId, setCompanyId] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [typeOfPayment, setTypeOfPayment] = useState('');
  const [paymentImage, setPaymentImage] = useState(null);
  const [isRsaSaleCompany, setIsRsaSaleCompany] = useState(false);

  useEffect(() => {
    const storedUserData = sessionStorage.getItem('user');
    if (!storedUserData) return;

    const parsedData = JSON.parse(storedUserData);
    const rsaSale = isRsaSaleContext(parsedData);

    setAgentId(parsedData.userId);
    setAgentName(parsedData.name);
    setCompanyId(parsedData.companyId || 'COMP001');
    setCompanyName(parsedData.companyName || 'Default Company');
    setIsRsaSaleCompany(rsaSale);

    const loadPackages = async () => {
      try {
        if (rsaSale) {
          // RSASALE company only — new GetAgentPackages API
          const response = await axios.post(
            `${BASE_URL}GetAgentPackages`,
            { AgentId: parsedData.userId },
            { headers: { 'Content-Type': 'application/json' } }
          );
          const ok =
            response.data?.responseCode === '0000' ||
            response.data?.ResponseCode === '0000' ||
            response.data?.success === true ||
            Array.isArray(response.data?.packages) ||
            Array.isArray(response.data?.Packages) ||
            Array.isArray(response.data?.data);

          if (ok) {
            const raw =
              response.data.packages ||
              response.data.Packages ||
              response.data.data ||
              [];
            setPackages(normalizeAgentPackages(raw, true));
          } else {
            Swal.fire('Error', 'Failed to load packages.', 'error');
          }
          return;
        }

        // Existing flow for all other companies
        const response = await axios.get(
          `${BASE_URL}RetailAgentGetPackage?id=${parsedData.userId}`
        );
        if (response.data.responseCode === '0000') {
          setPackages(response.data.packages || []);
        } else {
          Swal.fire('Error', 'Failed to load packages.', 'error');
        }
      } catch {
        Swal.fire('Error', 'An error occurred while fetching packages.', 'error');
      }
    };

    loadPackages();
  }, []);

  const selectedPackage = useMemo(
    () => packages.find((pkg) => String(pkg.packageid) === String(packageId)),
    [packages, packageId]
  );

  const isRsaPackage =
    isRsaSaleCompany ||
    String(selectedPackage?.productType || '').toUpperCase() === 'RSA';

  const isFikrFreeSale =
    String(selectedPackage?.productType || '').toUpperCase() === 'FIKRFREESALE';

  useEffect(() => {
    // Reset RSA-only fields when package changes away from RSA
    if (!isRsaPackage) {
      setTypeOfPayment('');
      setPaymentImage(null);
    }
  }, [isRsaPackage, packageId]);

  const submitRsaActivation = async (pkg) => {
    const rsaServiceId =
      String(pkg?.productType || '').toUpperCase() === 'RSA'
        ? pkg?.serviceid ?? pkg?.serviceId ?? pkg?.ServiceId ?? pkg?.packageid
        : pkg?.packageid;

    const formData = new FormData();
    formData.append('MobileNumber', mobileNo);
    formData.append('CompanyId', agentId);
    formData.append('Name', CustomerName || '');
    formData.append('CNIC', cnic || '');
    formData.append('Gender', gender || '');
    formData.append('ServiceId', String(rsaServiceId));
    formData.append('ServiceName', pkg.packageName || '');
    formData.append('TypeOfPayment', typeOfPayment);

    if (paymentImage) {
      formData.append('img', paymentImage);
    }

    const payloadPreview = {
      MobileNumber: mobileNo,
      CompanyId: agentId,
      Name: CustomerName || '',
      CNIC: cnic || '',
      Gender: gender || '',
      ServiceId: String(rsaServiceId),
      ServiceName: pkg.packageName || '',
      TypeOfPayment: typeOfPayment,
      img: paymentImage ? paymentImage.name : null,
    };
    const activateUrl = getRsaActivateUrl(isRsaSaleCompany);
    console.log('RSA Activate request:', activateUrl, payloadPreview);

    try {
      // Do not set Content-Type manually — browser must add multipart boundary
      const response = await axios.post(activateUrl, formData);
      console.log('RSA Activate response:', response.status, response.data);
      return response.data;
    } catch (error) {
      console.error('RSA Activate API error:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        fullError: error,
      });
      throw error;
    }
  };

  const submitPaybridgeProcess = async (pkg) => {
    const amount = pkg?.packagePrice || 0;
    const productCode = pkg?.packageCode;
    const productType = pkg?.productType;
    const companyProduct = pkg?.packageName;

    const durationMatch = pkg?.packageDuration?.match(/\d+/);
    const durationDays = durationMatch ? parseInt(durationMatch[0], 10) : 30;

    const nowUtc = new Date();
    const pktNow = new Date(nowUtc.getTime() + (5 * 60 + nowUtc.getTimezoneOffset()) * 60000);
    pktNow.setDate(pktNow.getDate() + durationDays);

    const pad = (n) => String(n).padStart(2, '0');
    const expiredAt = `${pktNow.getFullYear()}-${pad(pktNow.getMonth() + 1)}-${pad(
      pktNow.getDate()
    )} 23:59:59`;

    const response = await axios.post(
      `${paybridge_Url}process`,
      {
        productType,
        CustomerName: CustomerName || null,
        Remarks: Remarks || null,
        agent_Name: agentName,
        agent_id: agentId,
        amount: String(amount),
        cnic: cnic || null,
        productCode,
        companyProduct,
        company_Name: companyName,
        company_id: companyId,
        msisdn: mobileNo,
        expired_at: expiredAt,
      },
      { headers: { 'Content-Type': 'application/json' } }
    );

    return { resBody: response.data, productType };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = [];
    if (!mobileNo) errors.push('Mobile Number is required.');
    if (!packageId) errors.push('Package is required.');

    if (isRsaPackage) {
      if (!CustomerName) errors.push('Name is required for RSA.');
      if (!gender) errors.push('Gender is required for RSA.');
      if (!typeOfPayment) errors.push('Type of Payment is required for RSA.');
      if (typeOfPayment && typeOfPayment !== 'Cash' && !paymentImage) {
        errors.push('Payment image is required for Wallet Payment and Card.');
      }
    }

    if (errors.length > 0) {
      Swal.fire('Error', errors.join('\n'), 'error');
      return;
    }

    if (!selectedPackage) {
      Swal.fire('Error', 'Selected package was not found.', 'error');
      return;
    }

    setLoading(true);
    try {
      if (isRsaPackage) {
        const rsaRes = await submitRsaActivation(selectedPackage);
        setLoading(false);

        const { message: rsaMessage, transactionId: txnId, ok } =
          getRsaResponseDetails(rsaRes);

        if (ok) {
          Swal.fire({
            icon: 'success',
            title: 'RSA Activated',
            html: txnId
              ? `${rsaMessage || 'RSA activated successfully.'}<br/><small class="text-muted">Txn ID: ${txnId}</small>`
              : rsaMessage || 'RSA activated successfully.',
          }).then(() => {
            window.location.reload();
          });
        } else {
          console.error('RSA Activation Failed - response:', rsaRes);
          Swal.fire({
            icon: 'error',
            title: 'RSA Activation Failed',
            text:
              rsaMessage ||
              'The RSA service rejected the request. Please verify the customer details and try again.',
          });
        }
        return;
      }

      const { resBody, productType } = await submitPaybridgeProcess(selectedPackage);
      setLoading(false);

      const productTypeUpper = String(productType || '').toUpperCase();
      const ppCode = resBody?.data?.pp_ResponseCode;

      // FIKRFREESALE only — alert by documented response codes
      if (productTypeUpper === 'FIKRFREESALE') {
        console.log('FIKRFREESALE response:', resBody);
        if (showPaybridgeCodeAlert(resBody)) return;

        Swal.fire({
          icon: 'error',
          title: 'Transaction Failed',
          text: resBody.message || 'Unexpected response for FIKRFREESALE.',
        });
        return;
      }

      if (resBody.status === 'ALREADY_SUBSCRIBED') {
        Swal.fire({
          icon: 'warning',
          title: 'Already Subscribed',
          text: resBody.message || 'Customer already subscribed within 365 days.',
        });
      } else if (productTypeUpper === 'TPL_PAYMENT') {
        if (resBody.success) {
          Swal.fire({
            icon: 'success',
            title: 'Payment Link Sent',
            text: resBody.message || 'Payment URL generated and SMS sent to customer.',
          }).then(() => {
            window.location.reload();
          });
        } else {
          console.error('TPL payment failed - full response:', resBody);
          let failureMsg = resBody.message || 'Payment failed. Please try again.';
          if (/unknown productType/i.test(failureMsg)) {
            failureMsg = 'Unknown productType';
          }
          Swal.fire({
            icon: 'error',
            title: 'Transaction Failed',
            text: failureMsg,
          });
        }
      } else if (resBody.success && resBody.response_code === '000' && ppCode === '000') {
        Swal.fire({
          icon: 'success',
          title: 'Payment Successful',
          text: resBody.message || 'Payment processed successfully.',
        }).then(() => {
          window.location.reload();
        });
      } else {
        console.error('Payment failed - full response:', resBody);

        let failureText =
          resBody.message ||
          resBody?.data?.pp_ResponseMessage ||
          'Payment failed. Please try again.';
        if (/unknown productType/i.test(failureText)) {
          failureText = 'Unknown productType';
        }
        const refNo = resBody.txnRefNo || resBody?.data?.pp_TxnRefNo;

        Swal.fire({
          icon: 'error',
          title: 'Transaction Failed',
          html: refNo
            ? `${failureText}<br/><small class="text-muted">Ref No: ${refNo}</small>`
            : failureText,
        });
      }
    } catch (error) {
      setLoading(false);
      console.error('Sales submit error:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        fullError: error,
      });

      const errBody = error.response?.data;
      const selectedType = String(selectedPackage?.productType || '').toUpperCase();

      // Code-based alerts only for FIKRFREESALE
      if (selectedType === 'FIKRFREESALE' && errBody && showPaybridgeCodeAlert(errBody)) {
        return;
      }

      if (selectedType === 'FIKRFREESALE' && error.response?.status === 401) {
        Swal.fire({
          icon: 'error',
          title: 'UNAUTHORIZED',
          text:
            errBody?.message ||
            'Invalid or missing X-Auth-Key header or partner mismatch.',
        });
        return;
      }

      const apiMsg = getRsaErrorMessage(error);

      let displayMsg =
        apiMsg || error.message || 'An error occurred while processing payment.';

      if (
        !error.response &&
        String(error.message || '')
          .toLowerCase()
          .includes('network')
      ) {
        displayMsg = isRsaSaleCompany
          ? 'Unable to reach RSA activate API. Please verify the portal API is running and try again.'
          : 'RSA API blocked by browser CORS. Please enable CORS for this website on kz.webddocsystems.com, then try again.';
      }

      Swal.fire('Error', displayMsg, 'error');
    }
  };

  const paymentIcon = (opt) => {
    if (opt === 'Cash') return 'bi-cash-coin';
    if (opt === 'Wallet Payment') return 'bi-wallet2';
    return 'bi-credit-card';
  };

  return (
    <>
      <Header />
      <div
        className={
          isRsaSaleCompany
            ? 'rsa-sales-page'
            : 'container-fluid d-flex justify-content-center align-items-center'
        }
        style={isRsaSaleCompany ? undefined : { minHeight: '80vh' }}
      >
        <div className="container-fluid">
          <div className="row justify-content-center w-100 mx-0">
            <div className="col-12 col-sm-10 col-md-8 col-lg-6 col-xl-5 col-xxl-4">
              <div
                className={`${
                  isRsaSaleCompany ? 'card rsa-sales-shell' : 'card shadow-sm'
                }${loading ? ' co-card-loading' : ''}`}
              >
                {loading && (
                  <AppLoader
                    rsa={isRsaSaleCompany || isRsaPackage}
                    overlay
                    message={
                      isRsaPackage || isRsaSaleCompany
                        ? 'Activating RSA service...'
                        : 'Processing payment...'
                    }
                  />
                )}
                {isRsaSaleCompany ? (
                  <div className="rsa-sales-head">
                    <img
                      src="/RSA_Pakistan_Logo.png"
                      className="co-rsa-logo co-rsa-logo--lg mb-2"
                      alt="RSA Pakistan"
                    />
                    <span className="co-hero-badge mb-2">
                      <i className="bi bi-shield-check" />
                      RSA PAKISTAN
                    </span>
                    <h4>Activate RSA Service</h4>
                    <p>24/7 Roadside Assistance — enter customer details</p>
                  </div>
                ) : null}

                <div className={isRsaSaleCompany ? 'rsa-sales-body' : 'card-body'}>
                  {!isRsaSaleCompany && (
                    <div className="text-center mb-4">
                      <h4>Register Customer</h4>
                      {isRsaPackage && (
                        <span className="badge text-bg-info">RSA Package</span>
                      )}
                      {isFikrFreeSale && (
                        <span className="badge text-bg-success">FIKRFREESALE</span>
                      )}
                    </div>
                  )}

                  <form onSubmit={handleSubmit}>
                    <div className="row mb-3">
                      <div className="col-12 col-md-6">
                        <label className="form-label">
                          Name{isRsaPackage ? ' *' : ''}
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter full name"
                          value={CustomerName}
                          onChange={(e) => setCustomerName(e.target.value)}
                        />
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="form-label">Mobile No *</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="e.g. 03001234567"
                          value={mobileNo}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (/^\d{0,11}$/.test(value)) {
                              if (value.length <= 2) setMobileNo('03'.slice(0, value.length));
                              else if (value.startsWith('03')) setMobileNo(value);
                            }
                          }}
                        />
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-12 col-md-6">
                        <label className="form-label">
                          CNIC <span className="text-muted">(optional)</span>
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={13}
                          className="form-control"
                          placeholder="Enter 13 digit CNIC"
                          value={cnic}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (/^\d{0,13}$/.test(value)) setCnic(value);
                          }}
                        />
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="form-label">
                          Gender{isRsaPackage ? ' *' : ''}
                        </label>
                        <select
                          className="form-select"
                          value={gender}
                          onChange={(e) => setGender(e.target.value)}
                        >
                          <option value="">Select Gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-12">
                        <label className="form-label">
                          {isRsaSaleCompany ? 'RSA Package *' : 'Package *'}
                        </label>
                        <select
                          className="form-select"
                          value={packageId}
                          onChange={(e) => setPackageId(e.target.value)}
                        >
                          <option value="">Select Package</option>
                          {packages.map((pkg) => (
                            <option key={pkg.packageid} value={pkg.packageid}>
                              {pkg.packageName} - Rs.{pkg.packagePrice}
                              {!isRsaSaleCompany && pkg.productType
                                ? ` (${pkg.productType})`
                                : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {isRsaPackage && (
                      <>
                        <div className="row mb-3">
                          <div className="col-12">
                            <label className="form-label">Type of Payment *</label>
                            {isRsaSaleCompany ? (
                              <div className="rsa-pay-options mt-1">
                                {TYPE_OF_PAYMENT_OPTIONS.map((opt) => (
                                  <button
                                    key={opt}
                                    type="button"
                                    className={`rsa-pay-option${
                                      typeOfPayment === opt ? ' active' : ''
                                    }`}
                                    onClick={() => {
                                      setTypeOfPayment(opt);
                                      if (opt === 'Cash') setPaymentImage(null);
                                    }}
                                  >
                                    <i className={`bi ${paymentIcon(opt)}`} />
                                    {opt}
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <select
                                className="form-select"
                                value={typeOfPayment}
                                onChange={(e) => {
                                  setTypeOfPayment(e.target.value);
                                  if (e.target.value === 'Cash') setPaymentImage(null);
                                }}
                              >
                                <option value="">Select Type of Payment</option>
                                {TYPE_OF_PAYMENT_OPTIONS.map((opt) => (
                                  <option key={opt} value={opt}>
                                    {opt}
                                  </option>
                                ))}
                              </select>
                            )}
                          </div>
                        </div>

                        {typeOfPayment && typeOfPayment !== 'Cash' && (
                          <div className="row mb-3">
                            <div className="col-12">
                              <label className="form-label">Payment Image *</label>
                              <input
                                type="file"
                                className="form-control"
                                accept="image/*"
                                onChange={(e) =>
                                  setPaymentImage(e.target.files?.[0] || null)
                                }
                              />
                              {paymentImage && (
                                <small className="text-muted d-block mt-1">
                                  Selected: {paymentImage.name}
                                </small>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {!isRsaPackage && (
                      <div className="mb-3">
                        <label className="form-label">Remarks</label>
                        <textarea
                          className="form-control"
                          rows="3"
                          placeholder="Enter remarks (optional details)"
                          value={Remarks}
                          onChange={(e) => setRemarks(e.target.value)}
                        />
                      </div>
                    )}

                    <button
                      type="submit"
                      className={`btn w-100 ${
                        isRsaSaleCompany ? 'btn-rsa-activate text-white' : 'btn-primary'
                      }`}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Spinner size="sm" /> Processing...
                        </>
                      ) : isRsaPackage ? (
                        'Activate RSA'
                      ) : (
                        'Pay & Register'
                      )}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sales;
